import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Loader2, Lock, MoreVertical, Eye, Edit2, Trash2 } from "lucide-react";
import { useRoles, useDeleteRole, useHasPermission } from "@/hooks/rbac";
import { Role } from "@/types/rbac";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import RoleWizard from "./RoleWizard";
import RoleTemplateCard from "./RoleTemplateCard";
import EmptyStateRoles from "./EmptyStateRoles";
import { RoleViewDialog } from "./RoleViewDialog";
import { DataTable } from "@/components/ui/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

import { DateTimeCell } from "@/components/ui/table-cells";

interface RolesListProps {
  searchQuery?: string;
  view: "grid" | "list";
  showCreateButton?: boolean;
  onCreateRole?: () => void;
}

export default function RolesList({ 
  searchQuery = "", 
  view,
  showCreateButton = true,
  onCreateRole
}: RolesListProps) {
  const { data: roles = [], isLoading } = useRoles();
  const { mutate: deleteRole } = useDeleteRole();
  const { hasPermission: hasEditPermission } = useHasPermission('roles', 'edit');
  
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const handleEdit = (role: Role) => {
    setSelectedRole(role);
    setShowRoleDialog(true);
  };
  
  const handleDelete = (role: Role) => {
    setSelectedRole(role);
    setShowDeleteDialog(true);
  };
  
  const handleView = (role: Role) => {
    setSelectedRole(role);
    setShowViewDialog(true);
  };
  
  const confirmDelete = () => {
    if (selectedRole) {
      deleteRole({ id: selectedRole.id });
      setShowDeleteDialog(false);
      setSelectedRole(null);
    }
  };

  const handleCreateNew = () => {
    setSelectedRole(null);
    setShowRoleDialog(true);
  };

  // Filter roles based on search query
  const filteredRoles = roles.filter((role) =>
    role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    role.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Table columns for list view
  const columns: ColumnDef<Role>[] = [
    {
      accessorKey: "name",
      header: "Role Name",
      cell: ({ row }) => (
        <span className="font-medium">{row.original.name}</span>
      ),
    },
    {
      accessorKey: "is_system",
      header: "Type",
      cell: ({ row }) => (
        <Badge variant={row.original.is_system ? "secondary" : "outline"}>
          {row.original.is_system ? "System" : "User"}
        </Badge>
      ),
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.description || "No description"}
        </span>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => (
        <DateTimeCell date={row.original.created_at} format="date" />
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleView(row.original)}>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            {hasEditPermission && (
              <>
                <DropdownMenuItem onClick={() => handleEdit(row.original)}>
                  <Edit2 className="mr-2 h-4 w-4" />
                  Edit Role
                </DropdownMenuItem>
                {!row.original.is_system && (
                  <DropdownMenuItem 
                    onClick={() => handleDelete(row.original)}
                    className="text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Role
                  </DropdownMenuItem>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            Roles & Permissions {filteredRoles.length > 0 && (
              <span className="text-muted-foreground">({filteredRoles.length})</span>
            )}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Define access levels and permissions for your organization
          </p>
        </div>
        {showCreateButton && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button 
                    onClick={onCreateRole || handleCreateNew} 
                    disabled={!hasEditPermission}
                    className="flex items-center gap-2"
                  >
                    {hasEditPermission ? (
                      <Plus className="h-4 w-4" />
                    ) : (
                      <Lock className="h-4 w-4" />
                    )}
                    Create Role
                  </Button>
                </div>
              </TooltipTrigger>
              {!hasEditPermission && (
                <TooltipContent>
                  <p>You need edit permission on roles to create new roles</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {filteredRoles.length === 0 ? (
        searchQuery ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              No roles found matching "{searchQuery}".
            </p>
          </div>
        ) : (
          <EmptyStateRoles onCreateRole={handleCreateNew} />
        )
      ) : view === "grid" ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredRoles.map((role) => (
            <RoleTemplateCard
              key={role.id}
              role={role}
              userCount={0} // TODO: Add user count from backend
              onEdit={() => handleEdit(role)}
              onDelete={() => handleDelete(role)}
              onView={() => handleView(role)}
              canEdit={hasEditPermission}
              canDelete={hasEditPermission}
            />
          ))}
        </div>
      ) : (
        <DataTable
          data={filteredRoles}
          columns={columns}
          emptyMessage="No roles found."
        />
      )}

      <RoleWizard
        open={showRoleDialog}
        onOpenChange={setShowRoleDialog}
        role={selectedRole}
      />

      <RoleViewDialog
        role={selectedRole}
        isOpen={showViewDialog}
        onClose={() => {
          setShowViewDialog(false);
          setSelectedRole(null);
        }}
        userCount={0} // TODO: Add actual user count
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Role</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the "{selectedRole?.name}" role?
              This action cannot be undone and will remove all users from this role.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Role
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}